import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import axios from "axios";

import config from "../../config";
import { HPC_COLORS } from "../hpc/hpcTheme";
import {
  AmountField,
  ConfirmPaymentDialog,
  DateField,
  Field,
  FormSection,
  MethodButtons,
  ReceivePaymentButton,
  RecentPaymentsCard,
  ReferenceField,
  Stat,
  TableCell,
} from "./shared/PaymentShared";
import {
  METHODS_NEEDING_REFERENCE,
  amountError,
  amountInputValue,
  authConfig,
  displayValue,
  formatLabel,
  newTransactionId,
  printReceiptHtml,
  saveBlob,
} from "./shared/paymentUtils";
import {
  BreakdownTable,
  SUBSCRIPTION_TYPE_LABELS,
  StandingChip,
  TebaReceiptActions,
  TebaReceiptView,
  TebaRowReceiptButtons,
  formatDate,
  formatLkr,
  standingText,
  tebaReceiptPrintHtml,
} from "./tebaPaymentUi";

const API = `${config.apiBaseUrl}/payment-receive/teba`;

// Why a receipt SMS was not sent, in plain words.
const SMS_REASONS = {
  sms_disabled: "SMS is turned off.",
  missing_mobile_number: "No mobile number is saved for this family.",
  invalid_mobile_number: "No valid mobile number (check the member's mobile or WhatsApp number).",
  missing_gateway_url: "SMS is not set up on the server.",
  missing_user_id: "SMS is not set up on the server.",
  missing_api_key: "SMS is not set up on the server.",
  http_error: "The SMS provider rejected the message.",
  provider_rejected: "The SMS provider rejected the message.",
  gateway_exception: "The SMS provider could not be reached.",
  sms_exception: "Something went wrong while sending the SMS.",
};

const formFromSummary = (summary) => ({
  amount: summary.suggested_amount > 0 ? amountInputValue(summary.suggested_amount) : "",
  paymentDate: summary.today,
  paymentMethod: "cash",
  paymentReference: "",
  remarks: "",
});

/** Last day covered for a payment on ``isoDate``: the record_payment rule (shown before saving). */
const paidUntilFor = (subscriptionType, isoDate) => {
  if (subscriptionType === "day" || !isoDate) return null;
  const [year, month] = isoDate.split("-").map(Number);
  const expiry = subscriptionType === "annual" ? new Date(Date.UTC(year + 1, month - 1, 1)) : new Date(Date.UTC(year, month, 1));
  expiry.setUTCDate(expiry.getUTCDate() - 1);
  return expiry.toISOString().slice(0, 10);
};

const TebaPaymentPanel = ({ summary, onPaymentCompleted }) => {
  const { member, family, subscription, breakdown } = summary;
  const [form, setForm] = useState(() => formFromSummary(summary));
  const [errors, setErrors] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(true);
  const [receipt, setReceipt] = useState(null);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptError, setReceiptError] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const transactionIdRef = useRef(null);

  useEffect(() => {
    setForm(formFromSummary(summary));
    setErrors({});
  }, [summary]);

  const needsReference = METHODS_NEEDING_REFERENCE.includes(form.paymentMethod);
  const accentBorder = `1px solid ${alpha(HPC_COLORS.hpuOrange, 0.45)}`;
  const newPaidUntil = paidUntilFor(subscription.type, form.paymentDate);
  const owner = family.owner;
  const otherMembers = (family.members || []).filter((m) => m.membership_id !== member.membership_id);

  const update = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setErrors((current) => ({ ...current, [field]: "" }));
    setSubmitError("");
  };

  const chooseMethod = (_event, method) => {
    if (!method) return;
    setForm((current) => ({ ...current, paymentMethod: method }));
    setErrors((current) => ({ ...current, paymentMethod: "", paymentReference: "" }));
    setSubmitError("");
  };

  const validate = () => {
    const next = {};
    const amountProblem = amountError(form.amount);
    if (amountProblem) next.amount = amountProblem;
    if (!form.paymentDate) next.paymentDate = "Payment date is required.";
    else if (form.paymentDate > summary.today) next.paymentDate = "Payment date cannot be in the future.";
    if (!form.paymentMethod) next.paymentMethod = "Payment method is required.";
    if (needsReference && !form.paymentReference.trim()) next.paymentReference = "Payment reference is required for this method.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const openConfirmation = () => {
    setSubmitError("");
    if (validate()) setConfirmOpen(true);
  };

  const submit = async () => {
    if (submitting || !validate()) return;
    if (!transactionIdRef.current) transactionIdRef.current = newTransactionId();
    setSubmitting(true);
    setSubmitError("");
    try {
      const response = await axios.post(`${API}/payments`, {
        membership_id: member.membership_id,
        amount: form.amount,
        payment_method: form.paymentMethod,
        // The reference field is only shown for bank transfer and card.
        payment_reference: needsReference ? form.paymentReference.trim() || null : null,
        payment_date: form.paymentDate,
        remarks: form.remarks.trim() || null,
        transaction_id: transactionIdRef.current,
      }, authConfig());
      setConfirmOpen(false);
      setSuccess({ ...(response.data.payment || {}), sms: response.data.sms || null });
      setReceipt(null);
      transactionIdRef.current = null;
      if (onPaymentCompleted) await onPaymentCompleted();
    } catch (error) {
      setSubmitError(error.response?.data?.error || "Could not record the payment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const fetchReceipt = async (paymentId) => {
    if (receipt?.payment_id === paymentId) return receipt;
    setReceiptLoading(true);
    setReceiptError("");
    try {
      const response = await axios.get(`${API}/payments/${paymentId}/receipt`, authConfig());
      setReceipt(response.data.receipt || null);
      return response.data.receipt || null;
    } catch (error) {
      setReceiptError(error.response?.data?.error || "Could not load the receipt.");
      return null;
    } finally {
      setReceiptLoading(false);
    }
  };

  const viewReceipt = async (paymentId) => {
    if (await fetchReceipt(paymentId)) setReceiptOpen(true);
  };

  const printReceipt = async (paymentId) => {
    const next = await fetchReceipt(paymentId);
    if (next && !printReceiptHtml(tebaReceiptPrintHtml(next))) {
      setReceiptError("The receipt window was blocked. Please allow pop-ups and try again.");
    }
  };

  const downloadReceiptPdf = async (paymentId, receiptNumber) => {
    setReceiptLoading(true);
    setReceiptError("");
    try {
      const response = await axios.get(`${API}/payments/${paymentId}/receipt.pdf`, { ...authConfig(), responseType: "blob" });
      saveBlob(response.data, `${receiptNumber || "TEBA-receipt"}.pdf`);
    } catch (error) {
      setReceiptError("Could not download the receipt PDF.");
    } finally {
      setReceiptLoading(false);
    }
  };

  const copyLink = async () => {
    const url = success?.receipt_url || (await fetchReceipt(success?.payment_id))?.receipt_url;
    if (!url) {
      setReceiptError("The public receipt link is not set up (FRONTEND_PUBLIC_URL).");
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2500);
    } catch {
      setReceiptError(`Could not copy the link. Link: ${url}`);
    }
  };

  const smsText = (sms) => {
    if (!sms || sms.reason === "duplicate_request") return null;
    if (sms.sent) return "Receipt SMS sent.";
    return `Payment saved, but the receipt SMS was not sent: ${SMS_REASONS[sms.reason] || SMS_REASONS.provider_rejected}`;
  };

  return (
    <>
      <Card sx={{ mb: 2, border: accentBorder }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
            <Box>
              <Typography variant="h4" fontWeight={700}>{member.full_name || member.membership_id}</Typography>
              <Typography variant="body2" color="text.secondary">
                {member.membership_id}
                {member.mobile_phone ? ` · ${member.mobile_phone}` : ""}
                {member.branch_name ? ` · ${member.branch_name}` : ""}
              </Typography>
            </Box>
            <StandingChip standing={summary.standing} paidUntil={summary.paid_until} expiryDate={summary.expiry_date} />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Stat label="Subscription" value={subscription.name} />
            <Stat label="Type / fee" value={subscription.type ? `${SUBSCRIPTION_TYPE_LABELS[subscription.type] || subscription.type} · ${formatLkr(subscription.price)}` : null} />
            <Stat label="Paid until" value={summary.standing === "pay_per_day" ? "Pay per day" : summary.paid_until ? formatDate(summary.paid_until) : "No payment yet"} />
            <Stat label="Due now" value={formatLkr(summary.suggested_amount)} highlight />
          </Grid>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Field label="Family owner" value={owner ? `${owner.full_name || "—"} (${owner.membership_id || "—"})` : null} />
            <Field label="Branch" value={member.branch_name} />
            <Field label="Standing" value={standingText(summary.standing, summary.paid_until)} />
          </Grid>
          {otherMembers.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block">Family members (one subscription)</Typography>
              <Box display="flex" gap={0.75} flexWrap="wrap" mt={0.5}>
                {otherMembers.map((m) => (
                  <Chip key={m.membership_id} size="small" variant="outlined" label={`${m.full_name || "—"} · ${m.membership_id}${m.is_owner ? " · owner" : ""}`} />
                ))}
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" fontWeight={700} gutterBottom>Amount due</Typography>
          {breakdown.length > 0 ? (
            <>
              <BreakdownTable lines={breakdown} total={summary.suggested_amount} />
              {summary.invoice?.surcharge_percent > 0 && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Late surcharge {summary.invoice.surcharge_percent}% applies to unpaid past months, and to this month after day {summary.invoice.surcharge_day}.
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {summary.paid_until ? `Nothing is due. Fees are paid until ${formatDate(summary.paid_until)}.` : "Nothing is due now."}
            </Typography>
          )}
        </CardContent>
      </Card>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Payment recorded successfully. Receipt {displayValue(success.receipt_number)} · {formatLkr(success.amount)}
          {success.paid_until ? ` · Paid until ${formatDate(success.paid_until)}` : ""}.
          {smsText(success.sms) && <Typography variant="body2" sx={{ mt: 0.5 }}>{smsText(success.sms)}</Typography>}
          {linkCopied && <Typography variant="body2" sx={{ mt: 0.5 }}>Receipt link copied.</Typography>}
          <TebaReceiptActions
            onView={() => viewReceipt(success.payment_id)}
            onPrint={() => printReceipt(success.payment_id)}
            onPdf={() => downloadReceiptPdf(success.payment_id, success.receipt_number)}
            onCopyLink={copyLink}
            disabled={receiptLoading}
          />
        </Alert>
      )}
      {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}
      {receiptError && <Alert severity="error" sx={{ mb: 2 }}>{receiptError}</Alert>}

      {summary.can_pay ? (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h5" fontWeight={700}>Receive payment</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              Enter the payment details, then review them before saving.
            </Typography>
            {summary.standing === "free_card" && (
              <Alert severity="warning" sx={{ mb: 2.5 }}>
                Free card – no payment due. Every member of this family has a free card.
                {" "}You can still enter and record a payment if needed.
              </Alert>
            )}
            {summary.standing !== "free_card" && !(summary.suggested_amount > 0) && (
              <Alert severity="info" sx={{ mb: 2.5 }}>
                {summary.paid_until ? `Nothing due – paid until ${formatDate(summary.paid_until)}.` : "Nothing due."}
                {" "}You can still record an advance or other payment.
              </Alert>
            )}

            <FormSection title="Payment for">
              <TextField fullWidth value={summary.payment_for} label="Payment for" InputProps={{ readOnly: true }}
                helperText={`${subscription.name || "Subscription"} · paid per family`} />
            </FormSection>

            <FormSection title="Amount">
              <AmountField
                value={form.amount}
                onChange={update("amount")}
                error={errors.amount}
                helperText={summary.suggested_amount > 0
                  ? `Due now: ${formatLkr(summary.suggested_amount)}. Positive amount, maximum 2 decimals.`
                  : "Positive amount, maximum 2 decimals."}
              />
            </FormSection>

            <FormSection title="Method">
              <MethodButtons value={form.paymentMethod} onChange={chooseMethod} error={errors.paymentMethod} />
            </FormSection>

            {needsReference && (
              <ReferenceField value={form.paymentReference} onChange={update("paymentReference")} error={errors.paymentReference} />
            )}

            <DateField value={form.paymentDate} onChange={update("paymentDate")} error={errors.paymentDate} max={summary.today} />

            <FormSection title="Notes">
              <TextField fullWidth multiline minRows={2} label="Remarks" value={form.remarks} onChange={update("remarks")}
                inputProps={{ maxLength: 255 }} />
            </FormSection>

            <ReceivePaymentButton onClick={openConfirmation} disabled={submitting} />
          </CardContent>
        </Card>
      ) : (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This family has no subscription. Set a subscription on the member before taking a payment.
        </Alert>
      )}

      <RecentPaymentsCard
        rows={summary.recent_payments || []}
        loaded
        open={historyOpen}
        onToggle={() => setHistoryOpen((open) => !open)}
        headers={["Receipt", "Date", "Amount", "Method", "Paid until", "Received by", "Receipt actions"]}
        rowKey={(payment) => payment.payment_id}
        emptyText="No payments are recorded for this family yet."
        renderCells={(payment) => <>
          <TableCell>{displayValue(payment.receipt_number)}</TableCell>
          <TableCell>{formatDate(payment.payment_date)}</TableCell>
          <TableCell>{formatLkr(payment.amount)}</TableCell>
          <TableCell>{payment.payment_method ? formatLabel(payment.payment_method) : "—"}</TableCell>
          <TableCell>{payment.paid_until ? formatDate(payment.paid_until) : "—"}</TableCell>
          <TableCell>{displayValue(payment.received_by)}</TableCell>
          <TableCell align="right">
            <TebaRowReceiptButtons
              onView={() => viewReceipt(payment.payment_id)}
              onPrint={() => printReceipt(payment.payment_id)}
              onPdf={() => downloadReceiptPdf(payment.payment_id, payment.receipt_number)}
              disabled={receiptLoading}
            />
          </TableCell>
        </>}
      />

      <Dialog open={receiptOpen} onClose={() => setReceiptOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>TEBA Payment Receipt</DialogTitle>
        <DialogContent dividers>
          {receiptLoading && !receipt ? <CircularProgress size={24} /> : receipt && <TebaReceiptView receipt={receipt} />}
        </DialogContent>
        <DialogActions><Button color="inherit" onClick={() => setReceiptOpen(false)}>Close</Button></DialogActions>
      </Dialog>

      <ConfirmPaymentDialog open={confirmOpen} title="Confirm TEBA payment" onClose={() => setConfirmOpen(false)} onConfirm={submit}
        submitting={submitting} cancelColor="inherit">
        <Field label="Member ID" value={member.membership_id} />
        <Field label="Member name" value={member.full_name} />
        <Field label="Branch" value={member.branch_name} />
        <Field label="Payment for" value={summary.payment_for} />
        <Field label="Payment amount" value={formatLkr(form.amount)} />
        <Field label="Payment date" value={formatDate(form.paymentDate)} />
        <Field label="Payment method" value={formatLabel(form.paymentMethod)} />
        <Field label="Payment reference" value={needsReference ? form.paymentReference : ""} />
        <Field label="New paid until" value={subscription.type === "day" ? "Pay per day (no expiry)" : formatDate(newPaidUntil)} />
        <Field label="Remarks" value={form.remarks} />
      </ConfirmPaymentDialog>
    </>
  );
};

export default TebaPaymentPanel;
